import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import { alpha, Box, Grid, Paper, useTheme } from "@mui/material";
import FinancialReport from "../FinancialReport";
import OrderStats from "./OrderStats";
import DeliveryStats from "./DeliveryStats";
import ChannelsService from "../../../../api/channel";
import { cleanSpecialCharacters } from "../../../../extras";
import MerchantsService from "../../../../api/merchant";
import DriversService from "../../../../api/driver";
import ReportAverageTicketChart from "../charts/ReportAverageTicketChart";
import ReportIncomeChart from "../charts/ReportIncomeChart";

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

export default function CardsReportView({ startDate, endDate }) {
  const theme = useTheme();

  const [channels, setChannels] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [merchants, setMerchants] = useState([]);

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  const loadData = async () => {
    try {
      await loadChannelsData(startDate, endDate);
      await loadMerchantsData(startDate, endDate);
      await loadDriversData(startDate, endDate);
    } catch (e) {
      console.log(e);
    }
  };

  const loadChannelsData = async (dateStart, dateEnd) => {
    try {
      const params = {
        start_date: dateStart,
        end_date: dateEnd,
      };
      const res = await ChannelsService.channelAverageOrderTotal(params);

      const channelsWithOrderCount = res.data.map((channel) => {
        const title =
          channel.channel === "pos"
            ? "PDV"
            : channel.channel === "indoor"
            ? "MESAS"
            : channel.channel;
        return {
          ...channel,
          title: cleanSpecialCharacters(title),
        };
      });

      channelsWithOrderCount.sort(
        (a, b) => b.total_order_value - a.total_order_value
      );

      setChannels(channelsWithOrderCount);
    } catch (e) {
      console.error("Error loading channels data:", e);
      throw e;
    }
  };

  const loadMerchantsData = async (dateStart, dateEnd) => {
    try {
      const params = {
        start_date: dateStart,
        end_date: dateEnd,
      };
      const { data } = await MerchantsService.getMerchantsReportData(params);

      const merchantsWithTitle = data.map((merchant) => {
        return {
          ...merchant,
          title: merchant.merchant,
        };
      });

      merchantsWithTitle.sort((a, b) => b.order_count - a.order_count);

      setMerchants(merchantsWithTitle);
    } catch (e) {
      console.error("Error loading merchants data:", e);
      throw e;
    }
  };

  const loadDriversData = async (dateStart, dateEnd) => {
    try {
      const params = {
        start_date: dateStart,
        end_date: dateEnd,
      };
      const res = await DriversService.getDriversReportData(params);

      const driversWithTitle = res.data.map((driver) => {
        return {
          ...driver,
          title: driver.name,
          order_count: driver.order_count,
        };
      });

      driversWithTitle.sort((a, b) => b.order_count - a.order_count);

      setDrivers(driversWithTitle);
    } catch (e) {
      console.error("Error loading drivers data:", e);
      throw e;
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <FinancialReport startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <OrderStats startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <DeliveryStats startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                },
              }}
            >
              <ReportAverageTicketChart
                label="Ticket Médio por Canal"
                items={channels}
              />
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                },
              }}
            >
              <ReportIncomeChart
                items={channels}
                label="Faturamento por Canal"
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                },
              }}
            >
              <ReportIncomeChart
                label="Valor de pedidos por entregador"
                items={drivers}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
